import { useEffect } from "react"
import { Navigate, Route, useNavigate } from "react-router-dom"
import { FilterDrama, FilterNone, People, Person, Settings, StarBorder, VpnKey, Webhook, AttachFile, TableChart, AutoAwesomeOutlined } from "@mui/icons-material"
import { useSelector } from "react-redux"
import { isEmpty } from "lodash"
import { getRedirectUrl } from "helpers/routes"
import withFeatureGate from "hocs/withFeatureGate"
import withAiTermsGate from "hocs/withAiTermsGate"
import withAdminGate from "hocs/withAdminGate"

import PlanConditionalRedirect from "helpers/router/planConditionalRedirect"
import Reload from "helpers/router/reload"

import LoginLayout from "components/login/login-layout"
import Login from "modules/login"
import ForgotPassword from "modules/forgot-password"
import Accounts from "modules/accounts"
import ContactAdmin from "components/contact-admin"
import ResetPassword from "modules/reset-password"
import ActivateAccount from "modules/activate-account"
import LinkAccount from "modules/link-account"
import RefreshLoginPage from "./pages/refresh-login"
import UserActivation from "modules/user-activation"
import AccountNotActivated from "modules/account-not-activated"
import AccountMcpOnly from "modules/account-mcp-only"
import AccountTerms from "modules/account-terms"

import AlphaTheoryShare from "components/alpha-theory-share"

import AccountRoot from "modules/account-root"
import HomeLayout from "modules/home-layout"
import KellyRouteBridge from "connected-components/agent-chat"
import KellyAiHeader from "connected-components/agent-chat/header"
import Resources from "modules/resources"
import CreateResources from "connected-components/create-resource"
import MembersModule from "modules/members"
import MemberModule from "modules/member"
import MemberHeader from "modules/member/header"
import MemberDatasetEntitlementsModule from "modules/member-datasets"
import MemberDatasetEntitlementsHeader from "modules/member-datasets/header"
import TeamsModule from "modules/teams"
import TeamsHeader from "modules/teams/header"
import TeamMembersModule from "modules/team-members"
import TeamMembersHeader from "modules/team-members/header"
import TeamResourcesModule from "modules/team-resources"
import TeamResourcesHeader from "modules/team-resources/header"
import TeamTokensModule from "modules/team-tokens"
import TeamTokensHeader from "modules/team-tokens/header"
import TeamDatasetEntitlementsModule from "modules/team-datasets"
import TeamDatasetEntitlementsHeader from "modules/team-datasets/header"
import TokensModule from "modules/tokens"
import AccountPatsModule from "modules/account-pats"
import TokenModule from "modules/token"
import TokenHeader from "modules/token/header"
import TokenDatasetEntitlementsModule from "modules/token-datasets"
import TokenDatasetEntitlementsHeader from "modules/token-datasets/header"
import ResourceTeamsModule from "modules/resource-teams"
import ResourceTeamsHeader from "modules/resource-teams/header"
import ResourceMembersModule from "modules/resource-members"
import ResourceMembersHeader from "modules/resource-members/header"
import ResourceTokensModule from "modules/resource-tokens"
import ResourceTokensHeader from "modules/resource-tokens/header"
import ResourceApplicationsModule from "modules/resource-applications"
import ResourceApplicationsHeader from "modules/resource-applications/header"
import DataLibraryModule from "modules/data-cloud"
import CreateSolutionHeaderActions from "connected-components/solutions/components/create-solution-header-actions"
import SolutionsModule from "modules/solutions"
import AgenticWorkflowsModule from "modules/agentic-workflows"
import DatasetMembers from "modules/dataset-members"
import DatasetTeams from "modules/dataset-teams"
import DatasetTokens from "modules/dataset-tokens"
import DatasetReport from "modules/dataset-report"
import DatasetReportHeader from "components/dataset-report-header"

import MarketTools from "modules/market-tools"
import FactorTrends from "modules/factor-trends"
import FactorTrendsPerformance from "modules/factor-trends-performance"
import FactorTrendsRisk from "modules/factor-trends-risk"
import MarketToolsSecurityFinder from "modules/market-tools-securities-finder"

import AccountSettingsPage from "modules/account-settings-page"
import PlatformModule from "modules/platform"
import UserProfileModule from "modules/user-profile"

import AlphaTheoryModule from "modules/alpha-theory"
import AlphaTheoryHeader from "modules/alpha-theory/header"
import EzeModule from "modules/eze"
import EzeHeader from "modules/eze/header"
import ApplicationModule from "modules/application"
import ApplicationHeader from "modules/application/header"

import AnalyzeCompositeSecurity from "modules/analyze-composite-security"
import AnalyzeCompositeSecurityHeader from "modules/analyze-composite-security/header"

import PortfolioRoot from "modules/portfolio-root"

// Wrapped portfolio screens with AI context HOC adapters
// Each screen explicitly declares what dates are used for AI teammate
import {
  WrappedOverviewPerformance,
  WrappedOverviewRisk,
  WrappedAnalyzePerformance,
  WrappedAnalyzeHistoricalExposure,
  WrappedAnalyzeComposition,
  WrappedAnalyzeRisk,
  WrappedAnalyzeForecast,
  WrappedAnalyzeFactorSensitivity,
  WrappedAssetAnalytics,
  WrappedAnalyzeFactors,
  WrappedIntraday,
  WrappedOptimization,
  WrappedRebalanceModule,
  WrappedIntradayRebalance,
  WrappedExperimentsModule,
  WrappedEditExperiment,
  WrappedPortfolioCompareAdapter,
} from "modules/portfolio-root/screens"

import CompareExposure from "connected-components/compare/templates/exposure"
import ComparePerformance from "connected-components/compare/templates/performance"
import CompareRisk from "connected-components/compare/templates/risk"
import CompareFactorSensitivity from "connected-components/compare/templates/factor-sensitivity"
import CompareContentSet from "connected-components/compare/templates/content-set"
import CompareComposition from "connected-components/compare/templates/composition"

import NotFound from "components/404"
import ErrorPage from "./pages/error"

import WatchlistRoot from "modules/watchlist-root"
import WatchlistModule from "modules/watchlist"

import ResearchTopicRoot from "modules/research-topic-root"
import ResearchTopicHeader from "modules/research-topic-root/header"
import ResearchTopicSubnav from "modules/research-topic-root/subnav"
import ResearchTopic from "modules/research-topic"
import SmartTrade from "modules/smart-trade"

import PortfolioPositions from "modules/portfolio-positions"
import PortfolioPositionsHeader from "modules/portfolio-positions/header"
import SwapPositions from "modules/swap-positions"
import SwapPositionsHeader from "modules/swap-positions/header"
import WatchlistPositions from "modules/watchlist-positions"
import WatchlistPositionsHeader from "modules/watchlist-positions/header"
import ExperimentPositions from "modules/experiment-positions"
import ExperimentPositionsHeader from "modules/experiment-positions/header"
import AISummaryPopout from "modules/ai-summary/components/popout"
import RedirectWithParams from "helpers/router/redirectWithParams"
import PreferenceRedirect from "helpers/router/preferenceRedirect"
import PlanConditionalPreferenceRedirect from "helpers/router/planConditionalPreferenceRedirect"

import DashboardHomeLayout from "connected-components/dashboard/layout"
import DashboardContent from "connected-components/dashboard/dashboard-content"
import Dashboard from "connected-components/dashboard"
import DashboardHeader from "connected-components/dashboard/header"
import SigmaEmbed from "connected-components/sigma-embed"
import { SURFACE_TYPES } from "modules/account-root/landing-preference"

function indexRedirect(to) {
  return <Route index element={<Navigate to={to} replace />} />
}

function indexRedirectWithParams(to) {
  return <Route index element={<RedirectWithParams to={to} />} />
}

function redirect(from, to) {
  return <Route path={from} element={<Navigate to={`../${to}`} replace />} />
}

function redirectWithParams(path, to) {
  return <Route path={path} element={<RedirectWithParams to={`../${to}`} />} />
}

function RootRedirector() {
  const navigate = useNavigate()
  const user = useSelector(state => state.user)

  useEffect(() => {
    if (isEmpty(user)) {
      // If we're not logged in, redirect to login
      navigate(`/login${window.location.search}`)
    } else {
      // If user already loaded, redirect as appropriate
      const url = getRedirectUrl(user)
      navigate(url)
    }
  }, [user, navigate])
}

export default function Routes() {
  return <>
    <Route path="admin/*" Component={Reload} />
    <Route path="partners/*" Component={Reload} />
    <Route path="graphiql/*" Component={Reload} />
    <Route path="/" Component={LoginLayout}>
      <Route index Component={RootRedirector} />
      <Route path="login" Component={Login} />
      <Route path="accounts" Component={Accounts} />
      <Route path="contact-admin" Component={ContactAdmin} />
      <Route path="forgot-password" Component={ForgotPassword} />
      <Route path="reset-password/:token" Component={ResetPassword} />
      <Route path="activate-account/:token" Component={ActivateAccount} handle={{ sidebar: true }} />
      <Route path="link-account" Component={LinkAccount} />
      <Route path="refresh-login" Component={RefreshLoginPage} />
    </Route>
    <Route path="share/:alphaTheoryShareId" Component={AlphaTheoryShare} />
    <Route path=":accountValue" Component={AccountRoot}>
      {/* kellyHost: false — terminal shells and a popout, none of them reachable from a page holding a
          conversation. KellyChatGate reads this off the matched routes rather than keeping its own path list. */}
      <Route path="account-not-activated" Component={LoginLayout} handle={{ kellyHost: false, title: "Account not activated" }}>
        <Route index Component={AccountNotActivated} />
      </Route>
      <Route path="mcp-only" Component={LoginLayout} handle={{ kellyHost: false, title: "MCP access only" }}>
        <Route index Component={AccountMcpOnly} />
      </Route>
      <Route path="terms-and-conditions" Component={LoginLayout} handle={{ kellyHost: false, title: "Terms and conditions" }}>
        <Route index Component={AccountTerms} />
      </Route>
      <Route path="ai-summary/:summaryKey" Component={AISummaryPopout} handle={{ kellyHost: false, title: "AI summary" }} />
      <Route path="graphiql/*" Component={Reload} />
      <Route path="user-activation/:token" Component={UserActivation} handle={{ title: "User activation" }} />
      <Route path="applications" Component={HomeLayout} handle={{ title: "Applications" }}>
        <Route index element={<NotFound layoutDeactivated />} />
        <Route path="alpha-theory" Component={AlphaTheoryModule} handle={{ header: AlphaTheoryHeader, title: "Alpha Theory" }} />
        <Route path="eze" Component={EzeModule} handle={{ header: EzeHeader, title: "Eze" }} />
        <Route path=":app" Component={ApplicationModule} handle={{ header: ApplicationHeader }} />
      </Route>
      <Route path="home" Component={HomeLayout}>
        {indexRedirect("resources")}
        {redirect("portfolios", "resources")}
        {redirect("baskets", "resources")}
        {redirect("watchlists", "resources")}
        <Route
          path="resources"
          index
          Component={Resources}
          handle={{
            headerIcon: FilterNone,
            headerTitle: "Resources",
            headerActions: [CreateResources],
          }}
        />
        <Route
          path="favorites"
          element={<Resources favorites />}
          handle={{
            headerIcon: StarBorder,
            headerTitle: "Favorites",
            headerActions: [CreateResources],
          }}
        />
        <Route
          path="members"
          Component={MembersModule}
          handle={{
            headerIcon: Person,
            headerTitle: "Members",
          }}
        />
        <Route path="members/:memberId">
          {indexRedirect("permissions")}
          <Route path="permissions" Component={MemberModule} handle={{ header: MemberHeader, title: "Member permissions" }} />
          <Route path="entitlements" Component={MemberDatasetEntitlementsModule} handle={{ header: MemberDatasetEntitlementsHeader, title: "Member entitlements" }} />
        </Route>
        <Route path="teams" Component={TeamsModule} handle={{ header: TeamsHeader, title: "Teams" }} />
        <Route path="teams/:teamAlias">
          {indexRedirect("members")}
          <Route path="members" Component={TeamMembersModule} handle={{ header: TeamMembersHeader, title: "Team members" }} />
          <Route path="permissions" Component={TeamResourcesModule} handle={{ header: TeamResourcesHeader, title: "Team permissions" }} />
          <Route path="tokens" Component={TeamTokensModule} handle={{ header: TeamTokensHeader, title: "Team tokens" }} />
          <Route path="entitlements" Component={TeamDatasetEntitlementsModule} handle={{ header: TeamDatasetEntitlementsHeader, title: "Team entitlements" }} />
        </Route>
        <Route
          path="tokens"
          Component={TokensModule}
          handle={{
            headerIcon: People,
            headerTitle: "Tokens",
          }}
        />
        <Route path="tokens/:tokenId">
          {indexRedirect("permissions")}
          <Route path="permissions" Component={TokenModule} handle={{ header: TokenHeader, title: "Token permissions" }} />
          <Route path="entitlements" Component={TokenDatasetEntitlementsModule} handle={{ header: TokenDatasetEntitlementsHeader, title: "Token entitlements" }} />
        </Route>
        <Route
          path="personal-tokens"
          Component={withAdminGate(withFeatureGate(AccountPatsModule, "personal_tokens"))}
          handle={{
            headerIcon: VpnKey,
            headerTitle: "Personal Tokens",
          }}
        />
        <Route
          path="account"
          Component={AccountSettingsPage}
          handle={{
            headerIcon: Settings,
            headerTitle: "Account Settings",
          }}
        />
        <Route
          path="platform"
          Component={PlatformModule}
          handle={{
            headerIcon: Webhook,
            headerTitle: "Platform",
          }}
        />
        <Route
          path="profile"
          Component={UserProfileModule}
          handle={{
            headerIcon: Person,
            headerTitle: "Profile",
          }}
        />
        <Route path="market" Component={MarketTools} handle={{ noModelSelector: true, title: "Market" }}>
          {indexRedirectWithParams("factors/performance")}
          <Route path="factors" Component={FactorTrends}>
            {indexRedirectWithParams("performance")}
            <Route path="performance" Component={FactorTrendsPerformance} handle={{ title: "Market factor performance" }} />
            <Route path="risk" Component={FactorTrendsRisk} handle={{ title: "Market factor risk" }} />
          </Route>
          <Route path="securities" Component={MarketToolsSecurityFinder} handle={{ title: "Security finder" }} />
        </Route>
        <Route
          path="data-cloud"
          handle={{
            headerIcon: FilterDrama,
            headerTitle: "Data cloud",
          }}
        >
          {indexRedirect("browse")}
          <Route path="browse" Component={DataLibraryModule} handle={{ title: "Browse datasets" }} />
          <Route path="active" Component={DataLibraryModule} handle={{ title: "Active datasets" }} />
        </Route>
        <Route path="data-cloud/dataset/:datasetId">
          {indexRedirect("members")}
          <Route path="members" Component={DatasetMembers} handle={{ header: DatasetReportHeader, title: "Dataset members" }} />
          <Route path="teams" Component={DatasetTeams} handle={{ header: DatasetReportHeader, title: "Dataset teams" }} />
          <Route path="tokens" Component={DatasetTokens} handle={{ header: DatasetReportHeader, title: "Dataset tokens" }} />
          <Route path="report" Component={DatasetReport} handle={{ header: DatasetReportHeader, title: "Dataset report" }} />
        </Route>
        <Route
          path="solutions"
          Component={SolutionsModule}
          handle={{
            headerIcon: AttachFile,
            headerTitle: "Solutions",
            headerActions: [CreateSolutionHeaderActions],
          }}
        />
        <Route
          path="ai"
          Component={withFeatureGate(AgenticWorkflowsModule, "skills_delivery")}
          handle={{
            headerIcon: AutoAwesomeOutlined,
            headerTitle: "Agentic Workflows",
          }}
        />
      </Route>
      <Route path="solutions/:sigmaResourceId" Component={HomeLayout}>
        <Route
          index
          Component={SigmaEmbed}
          handle={{
            noModelSelector: true,
            headerIcon: TableChart,
            headerTitle: "Solution",
          }}
        />
      </Route>
      <Route path="composite/:assetType/:assetId" Component={HomeLayout} handle={{ noModelSelector: true, modelFromUrl: true, header: AnalyzeCompositeSecurityHeader, title: "Composite security" }}>
        {indexRedirectWithParams("assets")}
        <Route path="assets/:selectedView?" Component={AnalyzeCompositeSecurity} handle={{ title: "Composite assets" }} />
        <Route path="trends">
          {indexRedirectWithParams("performance")}
          <Route path=":selectedSubScreen/:subScreenId?" element={<AnalyzeCompositeSecurity path="trends" />} handle={{ title: "Composite trends" }} />
        </Route>
        <Route path="factors">
          {indexRedirectWithParams("performance")}
          <Route path=":selectedSubScreen" element={<AnalyzeCompositeSecurity path="factors" />} handle={{ title: "Composite factors" }} />
        </Route>
      </Route>
      <Route path="dashboard/:dashboardId?" Component={withFeatureGate(DashboardHomeLayout, "dashboard")} handle={{ noModelSelector: true, header: DashboardHeader, title: "Dashboard", landingSurface: SURFACE_TYPES.DASHBOARD }}>
        <Route index element={<DashboardContent><Dashboard /></DashboardContent>} />
      </Route>
      {/* The terms gate holds render until the permission resource resolves; wrapping the layout
          made it hold the whole viewport, so it gates the page inside the chrome instead. */}
      <Route path="kelly-ai" Component={withFeatureGate(HomeLayout, "managed_agent")} handle={{ noModelSelector: true, header: KellyAiHeader, title: "Kelly" }}>
        <Route path=":linkType?/:linkId?" Component={withAiTermsGate(KellyRouteBridge)} />
      </Route>
      {redirectWithParams("agent-chat", "kelly-ai")}
      <Route path="p/:portfolioId/upload" Component={HomeLayout} handle={{ header: PortfolioPositionsHeader, title: "Upload positions" }}>
        <Route path=":step?" Component={PortfolioPositions} />
      </Route>
      <Route path="t/:topicId/experiments/:experimentId/upload" Component={HomeLayout} handle={{ header: ExperimentPositionsHeader, title: "Upload experiment positions" }}>
        <Route path=":step?" Component={ExperimentPositions} />
      </Route>
      <Route path="p/:portfolioId/experiments/:experimentId/upload" Component={HomeLayout} handle={{ header: PortfolioPositionsHeader, title: "Upload experiment positions" }}>
        <Route path=":step?" Component={PortfolioPositions} />
      </Route>
      <Route path="baskets/:ticker/upload" Component={HomeLayout} handle={{ header: SwapPositionsHeader, title: "Upload basket positions" }}>
        <Route path=":step?" Component={SwapPositions} />
      </Route>
      <Route path="watchlist/:watchlistId/upload" Component={HomeLayout} handle={{ header: WatchlistPositionsHeader, title: "Upload watchlist positions" }}>
        <Route path=":step?" Component={WatchlistPositions} />
      </Route>
      {redirectWithParams("factors/litmus/:portfolioId", "p/:portfolioId/analyze")}
      <Route path="p/:portfolioId" Component={PortfolioRoot} handle={{ landingSurface: SURFACE_TYPES.PORTFOLIO }}>
        <Route index element={<PreferenceRedirect preferenceSubType="navigation.root" defaultRedirect="overview" />} />
        <Route path="overview">
          <Route index element={<PreferenceRedirect preferenceSubType="navigation.overview" defaultRedirect="performance" />} />
          <Route path="performance" Component={WrappedOverviewPerformance} handle={{ title: "Overview performance" }} />
          <Route path="risk" Component={WrappedOverviewRisk} handle={{ title: "Overview risk" }} />
        </Route>
        <Route path="analyze">
          <Route index element={<PreferenceRedirect preferenceSubType="navigation.analyze" defaultRedirect="trends" />} />

          {redirect("exposure", "trends/exposure")}
          {redirect("risk", "trends/risk")}
          {redirect("performance", "trends/performance")}
          <Route path="trends" handle={{ noContainer: true }}>
            <Route index element={<PreferenceRedirect preferenceSubType="navigation.analyze.trends" defaultRedirect="performance" />} />
            <Route path="performance" Component={WrappedAnalyzePerformance} handle={{ title: "Trends performance" }} />
            <Route path="exposure" Component={WrappedAnalyzeHistoricalExposure} handle={{ title: "Trends exposure" }} />
            <Route path="composition" Component={WrappedAnalyzeComposition} handle={{ title: "Trends composition" }} />
            <Route path="contentset/:contentSetId" Component={WrappedAnalyzeHistoricalExposure} handle={{ title: "Trends content set" }} />
            <Route path="exposure/historical" Component={WrappedAnalyzeHistoricalExposure} handle={{ title: "Trends historical exposure" }} />
            <Route path="forecast/:forecastId" Component={WrappedAnalyzeForecast} handle={{ title: "Trends forecast" }} />
            <Route path="risk" Component={WrappedAnalyzeRisk} handle={{ title: "Trends risk" }} />
            <Route path="factor-sensitivity">
              <Route index element={<PlanConditionalRedirect defaultRedirect="predicted" proRedirect="historical" />} />
              <Route path=":factorSensitivityType" Component={WrappedAnalyzeFactorSensitivity} handle={{ title: "Trends factor sensitivity" }} />
            </Route>
          </Route>
          <Route path="compare" Component={WrappedPortfolioCompareAdapter} handle={{ title: "Compare" }}>
            <Route index element={<PreferenceRedirect preferenceSubType="view" defaultRedirect="exposure" />} />
            <Route path="composition" Component={CompareComposition} handle={{ selectedView: "composition", title: "Compare composition" }} />
            <Route path="exposure" Component={CompareExposure} handle={{ selectedView: "exposure", title: "Compare exposure" }} />
            <Route path="performance" Component={ComparePerformance} handle={{ selectedView: "performance", isPerformance: true, title: "Compare performance" }} />
            <Route path="risk" Component={CompareRisk} handle={{ selectedView: "risk", title: "Compare risk" }} />
            <Route path="factor-sensitivity">
              <Route index element={<PlanConditionalRedirect defaultRedirect="predicted" proRedirect="historical" />} />
              <Route path="predicted" Component={CompareFactorSensitivity} handle={{ selectedView: "predictedFactorSensitivity", factorSensitivityType: "predicted", title: "Compare predicted factor sensitivity" }} />
              <Route path="historical" Component={CompareFactorSensitivity} handle={{ selectedView: "historicalFactorSensitivity", factorSensitivityType: "historical", title: "Compare historical factor sensitivity" }} />
            </Route>
            <Route path="contentset/:contentSetId" Component={CompareContentSet} handle={{ title: "Compare content set" }} />
          </Route>
          <Route path="assets">
            <Route index element={<PreferenceRedirect preferenceSubType="navigation.analyze.assets" defaultRedirect="summary" />} />
            <Route path=":selectedView" Component={WrappedAssetAnalytics} handle={{ title: "Assets" }} />
          </Route>
          <Route path="factors">
            <Route index element={<PlanConditionalPreferenceRedirect preferenceSubType="navigation.analyze.factors" defaultRedirect="risk" proRedirect="performance" />} />
            <Route path=":selectedScreen" Component={WrappedAnalyzeFactors} handle={{ title: "Factors" }} />
          </Route>
          <Route path="intraday">
            {indexRedirect("assets")}
            <Route path=":selectedScreen/:selectedView?" Component={WrappedIntraday} handle={{ title: "Intraday" }} />
          </Route>
        </Route>
        <Route path="rebalance">
          <Route index element={<PlanConditionalPreferenceRedirect preferenceSubType="navigation.rebalance" defaultRedirect="optimizer" proRedirect="experiments" />} />
          {redirect("manual", "simulator")}
          <Route path="optimizer" Component={WrappedOptimization} handle={{ title: "Optimizer" }} />
          <Route path="simulator" Component={WrappedRebalanceModule} handle={{ title: "Simulator" }} />
          <Route path="experiments" Component={WrappedExperimentsModule} handle={{ title: "Experiments" }} />
          <Route path="intraday">
            {indexRedirect("simulator")}
            <Route path="simulator" Component={WrappedIntradayRebalance} handle={{ title: "Intraday simulator" }} />
          </Route>
        </Route>
        {redirect("market", "../home/market")}
        {redirect("market/securities", "../home/market/securities")}
        {redirect("market/factors", "../home/market/factors")}
        <Route path="experiments/:experimentId/edit" Component={WrappedEditExperiment} handle={{ title: "Edit experiment" }} />
      </Route>
      <Route path="watchlist/:watchlistId" Component={WatchlistRoot}>
        <Route index Component={WatchlistModule} />
      </Route>
      <Route path="t/:topicId" Component={HomeLayout} handle={{ noModelSelector: true, header: ResearchTopicHeader, subnav: ResearchTopicSubnav }}>
        <Route Component={ResearchTopicRoot}>
          {indexRedirect("experiments")}
          <Route path="experiments">
            {indexRedirect("summary")}
            <Route path="exposure" Component={ResearchTopic} handle={{ title: "Topic exposure" }} />
            <Route path="risk" Component={ResearchTopic} handle={{ title: "Topic risk" }} />
            <Route path="performance" Component={ResearchTopic} handle={{ title: "Topic performance" }} />
            <Route path="composition" Component={ResearchTopic} handle={{ title: "Topic composition" }} />
            <Route path="summary" Component={ResearchTopic} handle={{ title: "Topic summary" }} />
            <Route path="contentset/:contentSetId" Component={ResearchTopic} handle={{ title: "Topic content set" }} />
          </Route>
          <Route path="smart-trade" handle={{ noSideMenu: true, title: "Smart trade", stateHeader: true }}>
            {indexRedirect("create")}
            <Route path=":experimentId/edit">
              {indexRedirect("finalize")}
              <Route path=":step" Component={SmartTrade} />
            </Route>
            <Route path="create">
              {indexRedirect("setup")}
              <Route path=":step" Component={SmartTrade} />
            </Route>
          </Route>
        </Route>
      </Route>
      <Route path=":resourceType/:resourceAlias/permissions" Component={HomeLayout}>
        {indexRedirect("members")}
        <Route path="members" Component={ResourceMembersModule} handle={{ header: ResourceMembersHeader, title: "Resource members" }} />
        <Route path="teams" Component={ResourceTeamsModule} handle={{ header: ResourceTeamsHeader, title: "Resource teams" }} />
        <Route path="applications" Component={ResourceApplicationsModule} handle={{ header: ResourceApplicationsHeader, title: "Resource applications" }} />
        <Route path="tokens" Component={ResourceTokensModule} handle={{ header: ResourceTokensHeader, title: "Resource tokens" }} />
      </Route>
    </Route>
    <Route path="404" Component={ErrorPage} />
    <Route path="*" element={<NotFound layoutDeactivated />} />
  </>
}
